import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  CircularProgress,
  Badge
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import {
  CustomPagination,
  PaymentModal,
  ResumeMixedExamModal,
  TableToggle,
  ExamTypeChip,
  CustomTableContainer,
  CustomTable,
  CustomTableHead,
  CustomTableRow,
  CustomTableBody,
  CustomTableCell,
  StartExamModal,
  McqConfirmationModal
} from '../../common';
import examService from '../../../infrastructure/services/examService';
import { getSynchronizedDate, parseSriLankanDateTime } from '../../../infrastructure/services/timeSyncService';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const parseInstructions = (instructions) => {
  if (!instructions) return [];
  return instructions
    .split('\n')
    .map((line) => line.replace(/^[•\u2022\u00b7\-*\s]+/, '').trim())
    .filter(Boolean);
};

const mapChildExamType = (type) => {
  const normalized = (type || '').toLowerCase();
  if (normalized === 'mcq') return 'MCQ';
  if (normalized === 'essay') return 'Essay';
  if (normalized === 'structure') return 'Structure';
  return type || '';
};

// Styles and type chips handled by common component ExamTypeChip

const CountdownStartButton = ({ exam, duration, schedule, onStartOrResume }) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (exam.scheduleType === 'now') {
      setTimeLeft(0);
      return;
    }

    const calculateTimeLeft = () => {
      const dateStr = exam.scheduledDate;
      const timeStr = exam.scheduledTime;
      if (!dateStr || !timeStr) return 0;

      try {
        const targetDate = parseSriLankanDateTime(dateStr, timeStr);
        if (!targetDate || isNaN(targetDate.getTime())) {
          return 0;
        }
        const now = getSynchronizedDate();
        const diff = targetDate.getTime() - now.getTime();
        return diff > 0 ? diff : 0;
      } catch (e) {
        return 0;
      }
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [exam.scheduledDate, exam.scheduledTime, exam.scheduleType]);

  const formatTimeLeft = (ms) => {
    const totalSecs = Math.floor(ms / 1000);
    const totalHours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;

    let parts = [];
    if (totalHours >= 24) {
      const days = Math.floor(totalHours / 24);
      const remainingHours = totalHours % 24;
      parts.push(`${days}d`);
      if (remainingHours > 0) {
        parts.push(`${remainingHours}h`);
      }
    } else if (totalHours > 0) {
      parts.push(`${totalHours}h`);
    }

    if (mins > 0 || totalHours > 0) {
      parts.push(`${String(mins).padStart(2, '0')}m`);
    }
    parts.push(`${String(secs).padStart(2, '0')}s`);

    return `Starts in ${parts.join(' ')}`;
  };

  if (timeLeft > 0) {
    return (
      <Button
        variant="outlined"
        size="small"
        disabled
        sx={{
          borderColor: '#94a3b8',
          color: '#64748b',
          fontWeight: 600,
          fontFamily: 'Poppins',
          fontSize: '12px',
          textTransform: 'none',
          borderRadius: '8px',
          px: 2.5,
          py: 0.5,
          height: '32px',
          whiteSpace: 'nowrap',
          '&.Mui-disabled': {
            borderColor: '#e2e8f0',
            color: '#64748b',
            bgcolor: '#f8fafc'
          }
        }}
      >
        {formatTimeLeft(timeLeft)}
      </Button>
    );
  }

  return (
    <Button
      variant="contained"
      size="small"
      onClick={() => onStartOrResume(exam, duration, schedule)}
      startIcon={<PlayArrowRoundedIcon />}
      sx={{
        bgcolor: '#FF502D',
        color: '#ffffff',
        fontWeight: 600,
        fontFamily: 'Poppins',
        fontSize: '12px',
        textTransform: 'none',
        borderRadius: '8px',
        px: 2.5,
        py: 0.5,
        height: '32px',
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        '&:hover': {
          bgcolor: '#e64525',
          boxShadow: 'none'
        }
      }}
    >
      Start Exam
    </Button>
  );
};

export default function UpcomingExamsTab({ subjectFilter, teacherName, onStatsFetched, refreshTrigger }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { teacherId } = useParams();
  const refreshExams = location.state?.refreshExams;
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [currentFilter, setCurrentFilter] = useState('all'); // 'all', 'ongoing', 'upcoming'
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedExamType, setSelectedExamType] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);

  const [mixedModalOpen, setMixedModalOpen] = useState(false);
  const [mixedModalExam, setMixedModalExam] = useState(null);

  // Start Exam Modal State for Resume Mixed Parts
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [selectedStartPart, setSelectedStartPart] = useState(null);

  // Payment Modal State
  const [selectedPaymentExam, setSelectedPaymentExam] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const buildMappedExam = (exam, examId, examType, duration, schedule) => ({
    id: exam.classExamId,
    examId,
    name: stripHtml(exam.examTitle),
    subject: exam.subjectName,
    topic: exam.className,
    duration,
    type: examType === 'mcq' ? 'MCQ' : examType === 'essay' ? 'Essay' : 'Structure',
    time: schedule,
    teacher: teacherName || 'Teacher',
    teacherId
  });


  const handleStartOrResume = (exam, duration, schedule) => {
    if (exam.inProgress && String(exam.examType).toLowerCase() === 'mixed') {
      setMixedModalExam(exam);
      setMixedModalOpen(true);
      return;
    }

    const mappedExam = buildMappedExam(exam, exam.examId, exam.examType, duration, schedule);
    const path = exam.inProgress ? 'take-exam' : 'exam-summary';
    navigate(`/exam-manager/${path}/${exam.classExamId}`, {
      state: {
        exam: mappedExam,
        fromRoute: `/exam-manager/exams/${exam.teacherId || ''}`
      }
    });
  };

  const handleSelectPart = (part) => {
    if (!part.inProgress && !part.hasAttended) {
      setSelectedStartPart(part);
      setStartModalOpen(true);
      return;
    }

    const exam = mixedModalExam;
    const mappedExam = buildMappedExam(
      exam,
      part.examId,
      part.examType,
      `${part.durationHours}H ${part.durationMinutes}M`,
      exam.scheduleType === 'now' ? 'Open Now' : `${exam.scheduledDate} ${exam.scheduledTime}`
    );
    setMixedModalOpen(false);
    navigate(`/exam-manager/take-exam/${exam.classExamId}`, { state: { exam: mappedExam } });
  };

  const handleConfirmStartPart = () => {
    if (!selectedStartPart) return;
    const part = selectedStartPart;
    const exam = mixedModalExam;
    const mappedExam = buildMappedExam(
      exam,
      part.examId,
      part.examType,
      `${part.durationHours}H ${part.durationMinutes}M`,
      exam.scheduleType === 'now' ? 'Open Now' : `${exam.scheduledDate} ${exam.scheduledTime}`
    );
    setStartModalOpen(false);
    setMixedModalOpen(false);
    setSelectedStartPart(null);
    navigate(`/exam-manager/take-exam/${exam.classExamId}`, { state: { exam: mappedExam } });
  };

  const checkIsPaymentLock = (exam) => {
    if (exam.isPaymentRequired) return true;

    const reason = (exam.lockReason || '').toUpperCase();
    if (['PAYMENT_REQUIRED', 'UNPAID', 'CLASS_PAYMENT', 'FEE_DUE', 'PAYMENT'].includes(reason)) {
      return true;
    }

    const msg = (exam.lockMessage || '').toLowerCase();
    if (msg.includes('pay') || msg.includes('fee')) {
      return true;
    }

    return false;
  };

  const handleOpenPaymentModal = (exam) => {
    setSelectedPaymentExam(exam);
    setPaymentOpen(true);
  };

  const handlePaymentSuccess = (classExamId) => {
    // Update local state to unlock the paid exam
    setExams((prevExams) =>
      prevExams.map((e) =>
        e.classExamId === classExamId
          ? {
            ...e,
            isLocked: false,
            isPaymentRequired: false,
            lockReason: null,
            lockMessage: null
          }
          : e
      )
    );
  };

  useEffect(() => {
    const fetchExams = async () => {
      setLoading(true);
      try {
        const data = await examService.getAssignedExams({
          teacherId,
          classId: selectedClassId || undefined,
          status: 'upcoming',
          examType: selectedExamType || undefined,
          sortBy: 'newest',
          page: currentPage,
          pageSize: 10
        });
        if (data && data.isSuccessful) {
          setExams(data.listContent || []);
          setTotalPages(data.paginationInfo?.totalPages || 1);
          setTotalItems(data.paginationInfo?.totalItems || 0);

          // Update available classes from the list
          if (data.listContent) {
            setAvailableClasses((prev) => {
              const seen = new Map(prev.map(c => [c.classId, c.className]));
              data.listContent.forEach(item => {
                if (item.classId && item.className) {
                  seen.set(item.classId, item.className);
                }
              });
              return Array.from(seen.entries()).map(([classId, className]) => ({ classId, className }));
            });
          }
          if (data.stats && onStatsFetched) {
            const statsMap = {};
            data.stats.forEach(item => {
              statsMap[item.id] = item.count;
            });
            onStatsFetched({
              total: statsMap.total ?? 0,
              completed: statsMap.completed ?? 0,
              ongoing: statsMap.ongoing ?? 0,
              upcoming: statsMap.upcoming ?? 0,
              missed: statsMap.missed ?? 0
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch upcoming exams:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, [teacherId, selectedClassId, selectedExamType, currentPage, onStatsFetched, refreshExams, refreshTrigger]);

  const filteredExams = exams.filter((exam) => {
    const title = stripHtml(exam.examTitle).toLowerCase();
    const search = searchQuery.toLowerCase();
    const matchesSearch =
      title.includes(search) ||
      (exam.classExamId && exam.classExamId.toLowerCase().includes(search)) ||
      (exam.subjectName && exam.subjectName.toLowerCase().includes(search)) ||
      (exam.className && exam.className.toLowerCase().includes(search));

    if (!matchesSearch) return false;

    if (currentFilter === 'ongoing') {
      return !exam.isUpcoming;
    }
    if (currentFilter === 'upcoming') {
      return exam.isUpcoming;
    }
    return true;
  });

  // Shared type chip handled by common component ExamTypeChip

  const getStatusChip = (status, lockReason, lockMessage) => {
    const isOpen = status === 'Open';
    const chipLabel = isOpen ? 'Open' : (lockMessage || lockReason || 'Locked');
    const isCompleted = chipLabel === 'Exam completed';

    let dotColor = '#ef4444';
    let bgColor = '#fef2f2';
    let textColor = '#ef4444';

    if (isOpen) {
      dotColor = '#10B981';
      bgColor = '#ECFDF5';
      textColor = '#10B981';
    } else if (isCompleted) {
      dotColor = '#3B82F6';
      bgColor = '#EFF6FF';
      textColor = '#3B82F6';
    }

    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: dotColor,
                flexShrink: 0
              }}
            />
            <span>{chipLabel}</span>
          </Box>
        }
        size="small"
        sx={{
          bgcolor: bgColor,
          color: textColor,
          fontWeight: 700,
          fontFamily: 'Poppins',
          borderRadius: '8px',
          px: 0.5,
          fontSize: '11px',
          height: '24px',
          '& .MuiChip-label': {
            px: 1,
            display: 'flex',
            alignItems: 'center'
          }
        }}
      />
    );
  };

  const safeCurrentPage = Math.min(currentPage, totalPages);

  return (
    <Box sx={{ py: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1.5px solid #e2e8f0',
          borderRadius: '24px',
          bgcolor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}
      >
        {/* Header and Actions Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3
          }}
        >
          {/* Title Block */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#262626', fontFamily: 'Poppins' }}>
                Current Exams
              </Typography>
              <Chip
                label={`${totalItems} Exams`}
                size="small"
                sx={{
                  bgcolor: '#FFF1EE',
                  color: '#FF502D',
                  fontWeight: 500,
                  fontSize: '12px',
                  fontFamily: 'Poppins',
                  borderRadius: '16px',
                  height: '22px'
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '13px',
                fontFamily: 'Poppins',
                mt: 0.5
              }}
            >
              Exams currently assigned to you for completion
            </Typography>
          </Box>

          {/* Search Input and Filter Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <TableToggle
              options={[
                { id: 'all', label: 'All' },
                { id: 'ongoing', label: 'Ongoing' },
                { id: 'upcoming', label: 'Upcoming' }
              ]}
              value={currentFilter}
              onChange={setCurrentFilter}
              layoutId="upcomingExamsActiveTabBackground"
            />
            <TextField
              placeholder="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              sx={{
                width: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#64748b' }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                  </InputAdornment>
                )
              }}
            />

            {(() => {
              const hasActiveFilters = !!selectedClassId || !!selectedExamType;
              return (
                <Button
                  variant="outlined"
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={
                    <Badge
                      variant="dot"
                      invisible={!hasActiveFilters}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#FF502D',
                          boxShadow: '0 0 0 2px #fff',
                        }
                      }}
                    >
                      <FilterListRoundedIcon />
                    </Badge>
                  }
                  sx={{
                    borderColor: (showFilters || hasActiveFilters) ? '#FF502D' : '#cbd5e1',
                    color: (showFilters || hasActiveFilters) ? '#FF502D' : '#64748b',
                    bgcolor: hasActiveFilters ? 'rgba(255, 80, 45, 0.03)' : 'transparent',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontFamily: 'Poppins',
                    fontSize: '13px',
                    height: '40px',
                    '&:hover': {
                      borderColor: '#FF502D',
                      color: '#FF502D',
                      bgcolor: 'rgba(255, 80, 45, 0.08)'
                    }
                  }}
                >
                  Filters
                </Button>
              );
            })()}
          </Box>
        </Box>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
              p: 2,
              borderRadius: '16px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0'
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="class-filter-label" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Class</InputLabel>
              <Select
                labelId="class-filter-label"
                value={selectedClassId}
                label="Class"
                onChange={(e) => {
                  setSelectedClassId(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  borderRadius: '10px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' }
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>All Classes</MenuItem>
                {availableClasses.map((cls) => (
                  <MenuItem key={cls.classId} value={cls.classId} sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>
                    {cls.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="type-filter-label" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Exam Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={selectedExamType}
                label="Exam Type"
                onChange={(e) => {
                  setSelectedExamType(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  borderRadius: '10px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' }
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>All Types</MenuItem>
                <MenuItem value="mcq" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>MCQ</MenuItem>
                <MenuItem value="essay" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Essay</MenuItem>
                <MenuItem value="structured" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Structure</MenuItem>
                <MenuItem value="mixed" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Mixed</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="text"
              onClick={() => {
                setSelectedClassId('');
                setSelectedExamType('');
                setCurrentPage(1);
              }}
              sx={{
                color: '#ef4444',
                fontWeight: 600,
                fontFamily: 'Poppins',
                fontSize: '13px',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05)' }
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {/* Table Area */}
        <CustomTableContainer sx={{ border: 'none', overflowX: 'auto' }}>
          <CustomTable size="medium">
            <CustomTableHead>
              <CustomTableRow>
                {[
                  { label: 'Exam Title' },
                  { label: 'Class and Subject' },
                  { label: 'Duration' },
                  { label: 'Available Schedule' },
                  { label: 'Type' },
                  { label: 'Status' }
                ].map((col) => (
                  <CustomTableCell
                    key={col.label}
                    sx={{
                      color: '#94a3b8',
                      fontWeight: 600,
                      fontSize: '12px',
                      fontFamily: 'Poppins',
                      borderBottom: '1px solid #f1f5f9',
                      py: 2,
                      bgcolor: '#ffffff'
                    }}
                  >
                    {col.label}
                  </CustomTableCell>
                ))}
                {/* Actions column */}
                <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2, bgcolor: '#ffffff' }} />
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <CustomTableRow key={index}>
                    {/* Exam Title */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="text" width="70%" height={24} sx={{ borderRadius: '4px' }} />
                    </CustomTableCell>
                    {/* Subject and Class */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px', mb: 0.5 }} />
                      <Skeleton variant="text" width="40%" height={16} sx={{ borderRadius: '4px' }} />
                    </CustomTableCell>
                    {/* Duration */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="text" width="50%" height={20} sx={{ borderRadius: '4px' }} />
                    </CustomTableCell>
                    {/* Available Schedule */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="text" width="80%" height={20} sx={{ borderRadius: '4px' }} />
                    </CustomTableCell>
                    {/* Type */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '8px' }} />
                    </CustomTableCell>
                    {/* Status */}
                    <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '8px' }} />
                    </CustomTableCell>
                    {/* Actions */}
                    <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="rectangular" width={90} height={32} sx={{ borderRadius: '8px', display: 'inline-block' }} />
                    </CustomTableCell>
                  </CustomTableRow>
                ))
              ) : filteredExams.length === 0 ? (
                <CustomTableRow>
                  <CustomTableCell colSpan={7} align="center" sx={{ py: 8, color: '#94a3b8', fontFamily: 'Poppins' }}>
                    {currentFilter === 'ongoing'
                      ? 'No ongoing exams found.'
                      : currentFilter === 'upcoming'
                        ? 'No upcoming exams found.'
                        : 'No exams found.'}
                  </CustomTableCell>
                </CustomTableRow>
              ) : (
                filteredExams.map((exam) => {
                  const isLocked = !exam.inProgress && (exam.isLocked || exam.isPaymentRequired || (exam.hasAttended && exam.gradingStatus === 'completed'));
                  const isPaymentLock = checkIsPaymentLock(exam);
                  const status = isLocked ? 'Locked' : 'Open';
                  const duration = `${exam.durationHours}H ${exam.durationMinutes}M`;
                  const schedule = exam.scheduleType === 'now' ? 'Open Now' : `${exam.scheduledDate} ${exam.scheduledTime}`;

                  let lockMsg = exam.lockMessage || exam.lockReason;
                  if (exam.hasAttended && exam.gradingStatus === 'completed' && !lockMsg) {
                    lockMsg = 'Exam completed';
                  } else if (exam.isPaymentRequired && !lockMsg) {
                    lockMsg = 'Class fee payment pending';
                  }

                  return (
                    <CustomTableRow key={exam.classExamId} hover sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                      {/* Exam Name and ID */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', fontFamily: 'Poppins' }}>
                            {stripHtml(exam.examTitle)}
                          </Typography>
                        </Box>
                      </CustomTableCell>

                      {/* Class and Subject */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', fontFamily: 'Poppins' }}>
                            {exam.className}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.25 }}>
                            {exam.subjectName}
                          </Typography>
                        </Box>
                      </CustomTableCell>

                      {/* Duration */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                        {duration}
                      </CustomTableCell>

                      {/* Available Schedule */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                        {schedule}
                      </CustomTableCell>

                      {/* Type */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                        <ExamTypeChip type={exam.examType} />
                      </CustomTableCell>

                      {/* Status */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                        {getStatusChip(status, exam.lockReason, lockMsg)}
                      </CustomTableCell>

                      {/* Action buttons */}
                      <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        {exam.isGrading ? (
                          <Chip
                            icon={
                              <CircularProgress
                                size={12}
                                thickness={5}
                                sx={{ color: '#3B82F6 !important' }}
                              />
                            }
                            label="Grading"
                            size="small"
                            sx={{
                              bgcolor: '#EFF6FF',
                              color: '#3B82F6',
                              fontWeight: 600,
                              fontFamily: 'Poppins',
                              fontSize: '11px',
                              borderRadius: '8px',
                              height: '32px',
                              px: 1.5,
                              '& .MuiChip-icon': {
                                marginLeft: '4px',
                                marginRight: '-2px',
                              }
                            }}
                          />
                        ) : exam.inProgress ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleStartOrResume(exam, duration, schedule)}
                            startIcon={<PlayArrowRoundedIcon />}
                            sx={{
                              bgcolor: '#ff502d',
                              color: '#ffffff',
                              fontWeight: 600,
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              textTransform: 'none',
                              borderRadius: '8px',
                              px: 2.5,
                              py: 0.5,
                              height: '32px',
                              boxShadow: 'none',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                bgcolor: '#e04324',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            Resume
                          </Button>
                        ) : !isLocked ? (
                          <CountdownStartButton
                            exam={exam}
                            duration={duration}
                            schedule={schedule}
                            onStartOrResume={handleStartOrResume}
                          />
                        ) : isPaymentLock ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenPaymentModal(exam)}
                            startIcon={<PaymentRoundedIcon />}
                            sx={{
                              bgcolor: '#FF502D',
                              color: '#ffffff',
                              fontWeight: 600,
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              textTransform: 'none',
                              borderRadius: '8px',
                              px: 2.5,
                              py: 0.5,
                              height: '32px',
                              boxShadow: 'none',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                bgcolor: '#e64525',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            Pay Now
                          </Button>
                        ) : (
                          <IconButton
                            size="small"
                            disabled
                            sx={{
                              bgcolor: '#f1f5f9',
                              color: '#94a3b8',
                              borderRadius: '8px',
                              width: 32,
                              height: 32
                            }}
                          >
                            <LockRoundedIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        )}
                      </CustomTableCell>
                    </CustomTableRow>
                  );
                })
              )}
            </CustomTableBody>
          </CustomTable>
        </CustomTableContainer>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <CustomPagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </Paper>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentOpen}
        onClose={() => {
          setPaymentOpen(false);
          setSelectedPaymentExam(null);
        }}
        exam={selectedPaymentExam}
        onPaymentSuccess={handlePaymentSuccess}
      />
      <ResumeMixedExamModal
        isOpen={mixedModalOpen}
        onClose={() => {
          setMixedModalOpen(false);
          setMixedModalExam(null);
        }}
        exam={mixedModalExam}
        onSelectPart={handleSelectPart}
      />
      {selectedStartPart && (
        mapChildExamType(selectedStartPart.examType) === 'MCQ' ? (
          <McqConfirmationModal
            isOpen={startModalOpen}
            onClose={() => {
              setStartModalOpen(false);
              setSelectedStartPart(null);
            }}
            onStart={handleConfirmStartPart}
          />
        ) : (
          <StartExamModal
            isOpen={startModalOpen}
            onClose={() => {
              setStartModalOpen(false);
              setSelectedStartPart(null);
            }}
            onStart={handleConfirmStartPart}
            duration={`${selectedStartPart.durationHours || 0} H ${selectedStartPart.durationMinutes || 0} Min`}
            questionsCount={`${selectedStartPart.questionCount || 0} Questions`}
            examType={mapChildExamType(selectedStartPart.examType)}
            instructions={parseInstructions(selectedStartPart.instructions)}
          />
        )
      )}
    </Box>
  );
}
